import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { forwardRef, Fragment, useContext, useEffect, useRef, useState } from 'react'

// others
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import {
  ExternalUser,
  ExternalUserCaseRole,
  ExternalUserRoleRole,
  useFetchAllExternalUsersLazyQuery
} from '../../../../../generated'
import { EXTERNAL_USER_CLIENT_CASE_ROLES, GRAPHQL_QUERY_POLICY } from '../../../../constants'
import { CaseContext } from '../../../../context'
import { ExternalUserWithClientNumberInterface } from '../../../../interfaceTypes'
import { menuHeight } from '../../../../theme/styleConstants'
import { titleize } from '../../../../utils'
import { formatAddress } from '../../../../utils/legalTeam'
import AddContact from '../../../externalUsers/AddContact'
import { ExternalUsersResult } from '../../../../interfaceTypes/common'

type ClientUserListRef = {
  appendClientUser: (user: ExternalUser) => void
}
export const ClientUserList = forwardRef<ClientUserListRef, {}>((props, ref) => {
  const [externalUsersData, setExternalUsersData] = useState<ExternalUsersResult>([])
  const [newExternalUser, setNewExternalUser] = useState<ExternalUser>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  const { contactRoleId, clientRoleId } = useContext(CaseContext)
  const [openModal, setOpenModal] = useState(false)

  const handleOpen = () => setOpenModal(true)
  const handleClose = () => setOpenModal(false)

  const {
    control,
    formState: { errors }
  } = useFormContext<{
    clientUsers: ExternalUserWithClientNumberInterface[]
  }>()
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'clientUsers'
  })
  const hasAppendedDefault = useRef(false)

  const [fetchAllExternalUsers, { loading }] = useFetchAllExternalUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onCompleted(data) {
      if (data) {
        const { fetchAllExternalUsers } = data

        if (fetchAllExternalUsers) {
          const { externalUsers, response, pagination } = fetchAllExternalUsers
          const { page } = pagination || {}
          const { status } = response || {}

          if (status === 200 && externalUsers) {
            externalUsersData && page !== 1
              ? setExternalUsersData([...externalUsersData, ...externalUsers])
              : setExternalUsersData(externalUsers)
          }
        }
      }
    }
  })

  const getOptions = (): ExternalUserWithClientNumberInterface[] =>
    externalUsersData?.map(user => ({
      id: user?.id || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      displayName: user?.displayName || '',
      email: user?.email || '',
      clientNumber: user?.clientNumber || '',
      workPhone: user?.phoneNumber || '',
      defaultBillingAddress: formatAddress(user?.addresses)
    })) ?? []

  useEffect(() => {
    if (fields.length === 0 && !hasAppendedDefault.current) {
      hasAppendedDefault.current = true
      append({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        clientNumber: '',
        caseRole: null,
        wantsBillDelivery: false,
        billDeliveryMethods: [],
        isBillParticipant: false
      })
    }
  }, [fields.length, append])

  useEffect(() => {
    if (newExternalUser?.id) {
      const focusedFieldIndex = fields.length ? fields.length - 1 : 0

      update(focusedFieldIndex, {
        id: newExternalUser.id,
        email: newExternalUser.email || '',
        firstName: newExternalUser.firstName,
        lastName: newExternalUser.lastName,
        clientNumber: newExternalUser.clientNumber || '',
        userRoleId: contactRoleId,
        caseRole: null
      })
    }
  }, [newExternalUser])

  useEffect(() => {
    fetchAllExternalUsers({
      variables: {
        externalUsersInput: {
          roles: [ExternalUserRoleRole.Contact, ExternalUserRoleRole.Client],

          paginationOptions: {
            limit: 20,
            page: currentPage
          },
          searchValue: debouncedSearchTerm
        }
      }
    })
    // eslint-disable-next-line
  }, [currentPage, debouncedSearchTerm])

  useEffect(() => {
    if (debouncedSearchTerm) {
      setCurrentPage(1)
    }
  }, [debouncedSearchTerm])

  const clientRow = (index: number) => {
    const name = `clientUsers.${index}` as const

    return (
      <Grid container item spacing={2.5} alignItems="center" sx={{ position: 'relative' }}>
        <Grid item xs={6} md={8}>
          <Controller
            name={name}
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => {
              return (
                <Autocomplete
                  fullWidth
                  options={getOptions() || []}
                  value={value?.id ? value : null}
                  filterOptions={createFilterOptions({ ignoreCase: true })}
                  loading={loading}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  clearOnEscape
                  onChange={(event, item, reason) => {
                    if (reason === 'clear' || !item) {
                      onChange(null)
                      return
                    }
                    const newUserRoleId = item?.clientNumber ? clientRoleId : contactRoleId
                    const newValue = {
                      ...value,
                      ...item,
                      userRoleId: newUserRoleId
                    }
                    onChange(newValue)
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'clear' || reason === 'reset') {
                      setSearchTerm('')
                      return
                    }
                    setSearchTerm(newInputValue)
                  }}
                  ListboxProps={{
                    onScroll: event => {
                      const listboxNode = event.currentTarget
                      if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight) {
                        setCurrentPage(currentPage + 1)
                      }
                    },
                    role: 'list-box',
                    style: {
                      maxHeight: '190px'
                    }
                  }}
                  getOptionLabel={item => {
                    const { firstName, lastName, displayName, email, clientNumber, id } = item || {}
                    const name = displayName?.trim() || `${firstName || ''} ${lastName || ''}`.trim()
                    const options = `${name} ${clientNumber ? `- #${clientNumber}` : ''}  ${email ? `(${email})` : ''}`
                    return id ? options : ''
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Contacts"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              )
            }}
          />
        </Grid>

        <Grid item xs={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <Controller
              name={`${name}.caseRole`}
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error: { message } = {} } }) => {
                return (
                  <Autocomplete
                    fullWidth
                    disableClearable
                    options={
                      EXTERNAL_USER_CLIENT_CASE_ROLES.filter(
                        role => role.value !== ExternalUserCaseRole.PrimaryClient
                      ) || []
                    }
                    onChange={(event, item) => {
                      onChange(item.value)
                    }}
                    value={EXTERNAL_USER_CLIENT_CASE_ROLES.find(item => item.value === value)}
                    ListboxProps={menuHeight}
                    getOptionLabel={value => value?.label || ''}
                    filterOptions={createFilterOptions({ ignoreCase: true })}
                    isOptionEqualToValue={(option, value) => {
                      return option?.value === value?.value
                    }}
                    renderInput={params => (
                      <TextField {...params} error={!!errors.clientUsers?.[index]?.caseRole} label="Matter Role" />
                    )}
                  />
                )
              }}
            />
          </FormControl>
        </Grid>

        {index > 0 && (
          <IconButton onClick={() => remove(index)} style={{ position: 'absolute', right: -42 }}>
            <ClearIcon color="primary" />
          </IconButton>
        )}

        <Grid md={8} />

        <Grid
          item
          md={4}
          xs={12}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{
            paddingY: '4px !important'
          }}
        >
          <FormHelperText error={!!errors.clientUsers?.[index]?.caseRole}>
            {typeof errors.clientUsers?.[index]?.caseRole?.message === 'string'
              ? errors.clientUsers?.[index]?.caseRole?.message
              : ' '}
          </FormHelperText>
        </Grid>

        <AddContact customOpen={openModal} handleCustomClose={handleClose} setNewExternalUser={setNewExternalUser} />
      </Grid>
    )
  }

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <Typography variant="h3" fontSize={14} fontWeight={700} color={'primary.main'} mt={{ xs: 3, md: 0 }}>
        Contacts
      </Typography>
      <FormControl variant="outlined" fullWidth>
        <Grid container>
          {fields.map((field, index) => (
            <Fragment key={field.id}>{clientRow(index)}</Fragment>
          ))}
        </Grid>

        <Grid
          item
          md={8}
          xs={12}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ pt: '0 !important', position: 'relative', bottom: 30 }}
        >
          <Button
            onClick={() =>
              append({
                id: '',
                firstName: '',
                lastName: '',
                email: '',
                clientNumber: '',
                caseRole: null,
                wantsBillDelivery: false,
                billDeliveryMethods: [],
                isBillParticipant: false
              })
            }
            startIcon={<AddIcon fontSize="small" />}
            variant="text"
            sx={{ fontSize: 11, pl: 1 }}
          >
            Add Contact
          </Button>

          <Box display="flex" alignItems="center">
            <Button size="small" variant="text" onClick={handleOpen}>
              <span style={{ fontSize: 11 }}>{titleize(`Create New`)}</span>
            </Button>
          </Box>
        </Grid>
      </FormControl>
    </Box>
  )
})
